import React, { useRef, useEffect, useState } from "react";

type OtpInputProps = {
	value: string;
	onChange: (value: string) => void;
	disabled?: boolean;
	placeholder?: string;
	className?: string;
};

function OtpInput({
	value,
	onChange,
	disabled = false,
	placeholder = "Enter verification code",
	className = "",
}: OtpInputProps) {
	// The activeIndex now primarily controls styling and cursor logic
	const [activeIndex, setActiveIndex] = useState(0);
	const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
	const hiddenInputRef = useRef<HTMLInputElement>(null);
	const otpLength = 8;

	// When a visible input is focused, set the active index and focus the real hidden input
	const handleFocus = (index: number) => {
		setActiveIndex(index);
		hiddenInputRef.current?.focus();
	};

	// The hidden input now handles ALL changes (typing and pasting)
	const handleHiddenInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (disabled) return;

		const typedValue = e.target.value;
		const oldOtp = value;

		// This handles multi-digit pastes.
		// It's a simple way to detect if more than one character was added.
		if (typedValue.length > oldOtp.length + 1) {
			const newOtp = typedValue.replace(/\D/g, "").slice(0, otpLength);
			onChange(newOtp);
			// Move cursor to the end of the pasted value
			setActiveIndex(Math.min(newOtp.length, otpLength - 1));
			return;
		}

		// This handles single-digit typing.
		if (typedValue.length > oldOtp.length) {
			const newChar = typedValue.charAt(activeIndex);

			// If a non-digit was somehow entered, ignore it.
			if (!/^\d$/.test(newChar)) {
				return;
			}

			// Manually construct the new string by overwriting the character.
			const newOtpArray = value.split("");
			newOtpArray[activeIndex] = newChar;
			const newOtp = newOtpArray.join("");

			onChange(newOtp);

			// Move cursor to the next position.
			setActiveIndex(Math.min(activeIndex + 1, otpLength - 1));
		}
	};

	// Handle backspace and navigation in the hidden input. This logic is more robust.
	const handleHiddenKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (disabled) return;

		if (e.key === "Backspace") {
			e.preventDefault();
			if (value[activeIndex]) {
				// If the current digit has a value, clear it and stay.
				const newOtpArray = value.split("");
				newOtpArray[activeIndex] = ""; // Use empty string to keep array length
				onChange(newOtpArray.join(""));
			} else if (activeIndex > 0) {
				// If the current digit is empty, move to the previous one and clear it.
				const newOtpArray = value.split("");
				newOtpArray[activeIndex - 1] = "";
				onChange(newOtpArray.join(""));
				setActiveIndex(activeIndex - 1);
			}
		} else if (e.key === "ArrowLeft") {
			e.preventDefault();
			setActiveIndex(Math.max(0, activeIndex - 1));
		} else if (e.key === "ArrowRight") {
			e.preventDefault();
			setActiveIndex(Math.min(otpLength - 1, activeIndex + 1));
		}
	};

	// Keep the hidden input's cursor in sync with our activeIndex state
	useEffect(() => {
		const input = hiddenInputRef.current;
		if (input) {
			input.focus();
			input.setSelectionRange(activeIndex, activeIndex);
		}
	}, [activeIndex]);

	return (
		<div
			className={`otp-input ${className}`}
			// Click anywhere on the container to focus the hidden input
			onClick={() => hiddenInputRef.current?.focus()}
			// Add keyboard support for accessibility
			tabIndex={0}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault();
					hiddenInputRef.current?.focus();
				}
			}}
			role="button"
			aria-label="OTP input field"
		>
			{/* The REAL input field. It's hidden but does all the work. */}
			<input
				ref={hiddenInputRef}
				type="text"
				inputMode="numeric"
				// The value of the hidden input is the complete OTP string
				value={value}
				onChange={handleHiddenInputChange}
				onKeyDown={handleHiddenKeyDown}
				disabled={disabled}
				style={{
					position: "absolute",
					opacity: 0,
					width: "1px",
					height: "1px",
					caretColor: "transparent", // Hide the blinking cursor
					pointerEvents: "none",
				}}
				aria-hidden="true"
			/>

			<div className="otp-input__container">
				{Array.from({ length: otpLength }, (_, index) => (
					<input
						key={index}
						ref={(el) => (inputRefs.current[index] = el)}
						type="text"
						readOnly // PREVENTS manual typing in these display inputs
						value={value[index] || ""}
						onFocus={() => handleFocus(index)} // Use onFocus to trigger our logic
						disabled={disabled}
						className={`otp-input__digit ${
							activeIndex === index ? "otp-input__digit--active" : ""
						} ${value[index] ? "otp-input__digit--filled" : ""}`}
						aria-label={`Digit ${index + 1}`}
					/>
				))}
			</div>
			{placeholder && !value && (
				<div className="otp-input__placeholder">{placeholder}</div>
			)}
		</div>
	);
}

export default OtpInput;
